import React, { useState, useContext, useEffect } from "react";
import { Button, TextField, Typography, CircularProgress } from "@mui/material";
import AuthContext from "../../../context/AuthContext";
import DrawerContext from "../../../context/DrawerContext";
import "./PricingDrawer.scss";

// Format period for display: use singular (e.g. "1 day", "1 month") when amount is 1; API returns plural
const formatPeriodDisplay = (amount, units) => {
  if (amount == null || units == null) return '';
  const num = typeof amount === 'string' ? parseInt(amount, 10) : amount;
  if (num === 1 && typeof units === 'string') {
    const singular = units.replace(/s$/, '');
    return `${amount} ${singular}`;
  }
  return `${amount} ${units}`;
};

const EditPricingDrawer = () => {
  const drawerCtx = useContext(DrawerContext);
  const authCtx = useContext(AuthContext);
  const isOpen = drawerCtx.details?.type === "edit-pricing";

  const [price, setPrice] = useState("");
  const [agentFee, setAgentFee] = useState("");
  const [isUpdating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen && drawerCtx.details) {
      setPrice(drawerCtx.details.price?.toString() || "");
      setAgentFee(drawerCtx.details.agentFee?.toString() || "");
    }
  }, [isOpen, drawerCtx.details]);

  const handleSave = () => {
    if (!drawerCtx.details?.id) {
      console.error("No rates ID found in context.");
      return;
    }

    // Validate that at least one field is provided
    if (!price && !agentFee) {
      alert("At least one field is required: price or agentFee");
      return;
    }

    if (!drawerCtx.details?.country) {
      console.error("No country ISO found in context.");
      alert("Country information is missing. Please try again.");
      return;
    }

    setUpdating(true);

    const payload = {};
    if (price) payload.price = parseFloat(price);
    if (agentFee) payload.agentFee = parseFloat(agentFee);
    payload.country = drawerCtx.details.country; // Include country ISO in payload

    fetch(`${process.env.REACT_APP_API_URI}/v3/rates/${drawerCtx.details.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authCtx.token}`
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((data) => {
            throw new Error(data.message || "Failed to update rates");
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data.status === "success") {
          drawerCtx.closeDrawer();
          drawerCtx.setPageUpdate({ page: "PricingTable" });
        }
      })
      .catch((error) => {
        console.error("Error updating rates:", error);
        alert(error.message || "Failed to update rates. Please try again.");
      })
      .finally(() => setUpdating(false));
  };

  if (!isOpen) return null;

  return (
    <div className={`pricing-drawer ${isOpen ? "open" : ""}`}>
      <div className="drawer-header">
        <Typography variant="h6">Edit Rates</Typography>
      </div>
      <div className="drawer-content">
        <div className="info-section">
          <Typography variant="body2" className="info-text">
            Period: <strong>{formatPeriodDisplay(drawerCtx.details?.amount, drawerCtx.details?.units)}</strong>
          </Typography>
          <Typography variant="body2" className="info-text">
            Days: <strong>{drawerCtx.details?.days}</strong>
          </Typography>
        </div>
        <TextField
          className="full-width-column"
          fullWidth
          label="Price"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          inputProps={{ step: "0.01", min: "0" }}
        />
        <TextField
          className="full-width-column"
          fullWidth
          label="Agent Fee"
          type="number"
          value={agentFee}
          onChange={(e) => setAgentFee(e.target.value)}
          inputProps={{ step: "0.01", min: "0" }}
        />
      </div>
      <div className="drawer-footer">
        <Button variant="contained" className="btn-primary cancel-button" onClick={drawerCtx.closeDrawer}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={(!price && !agentFee) || isUpdating} 
          className="btn-primary save-button"
        >
          {isUpdating ? <CircularProgress size={24} style={{ color: "#fff" }} /> : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default EditPricingDrawer;

